import { useEffect, useState } from "react";
import { Icon } from "../../components/Icon";
import type { MeetingCalendarSettings, MeetingDTO, MeetingEventDTO } from "../../../shared/types";
import { List, ListRow } from "../../components/List";
import { Tabs, useTabParam } from "../../components/Tabs";
import { Dialog } from "../../components/Dialog";
import { TextLink } from "../../components/TextLink";
import { Table } from "../../components/Table";
import { Badge, Button, ButtonLink, ErrorText, Field, inputCls, PageHeader, StatusBadge, timeAgo } from "../../components/ui";
import { get, patch, post, put } from "../../lib/api";
import { navigate } from "../../lib/router";
import { Switch } from "../../components/Switch";
import { GoogleAccountPanel, GoogleSignInBanner, type GoogleStatus } from "./GoogleAccount";
import { MeetingEnd } from "../../components/MeetingEnd";
import { END_WORDS, peopleWords } from "../../lib/meetings";

export type CalendarView = {
  settings: MeetingCalendarSettings;
  status: { connector: string | null; problem: string | null; lastSyncAt: string | null; lastSyncError: string | null };
  events: MeetingEventDTO[];
};

const STATUS_TONE: Record<MeetingDTO["status"], string> = {
  requested: "queued", joining: "running", waiting: "running", in_call: "running", processing: "running", done: "succeeded", failed: "failed", cancelled: "cancelled",
};

/** "in call"; a meeting the bot had no free slot for yet waits for one (AIT-366). */
const statusWords = (m: MeetingDTO) => (m.queuedAt && m.status === "requested" ? "waiting for a free slot" : m.status.replace("_", " "));

const PLATFORM_NAME: Record<MeetingEventDTO["platform"], string> = { google_meet: "Google Meet", zoom: "Zoom", teams: "Microsoft Teams", unknown: "Other" };

/** "Today · Tue 15 Sept", "Tomorrow · Wed 16 Sept", "Thu 17 Sept" */
const dayLabel = (iso: string) => {
  const date = new Date(iso);
  const label = date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  const days = Math.round((new Date(date.toDateString()).getTime() - new Date(new Date().toDateString()).getTime()) / 86_400_000);
  return days === 0 ? `Today · ${label}` : days === 1 ? `Tomorrow · ${label}` : days === -1 ? `Yesterday · ${label}` : label;
};
/** Pandas is in (or on its way into) the call. */
/** Statuses where Pandas is on its way to the call or already in it. */
export const ACTIVE: string[] = ["requested", "joining", "waiting", "in_call"];
const time = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
/** "30 min", "1h", "1h 30m" */
const duration = (from: string, to: string) => {
  const minutes = Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  return minutes % 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes / 60}h`;
};
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/**
 * Running the bot (AIT-335): every meeting it was sent to, the calendar it follows and its settings. A meeting is
 * private to the people in it, admins included: one you are not in is a row saying that it happened — its time,
 * platform, status and error, and how many are in it — which you can still retry or ask the bot to leave. Its content
 * is on its own page, under Meetings, for the people in it.
 */
export function AdminMeetings() {
  const [tab, setTab] = useTabParam(["upcoming", "history", "settings"] as const);
  const [enabled, setEnabled] = useState(true);
  const [meetings, setMeetings] = useState<MeetingDTO[]>([]);
  const [calendar, setCalendar] = useState<CalendarView | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState<GoogleStatus | null>(null);

  const loadMeetings = () => get<{ enabled: boolean; meetings: MeetingDTO[] }>("/api/meetings").then((r) => { setEnabled(r.enabled); setMeetings(r.meetings); }, (e) => setError(e.message));
  const loadCalendar = () => get<CalendarView>("/api/meetings/calendar").then(setCalendar, (e) => setError(e.message));

  useEffect(() => {
    void loadMeetings();
    void loadCalendar();
    // The banner when the Pandas Google account is signed out (AIT-365); a failure to read it is no banner.
    const loadGoogle = () => get<GoogleStatus>("/api/meetings/google-account").then(setGoogle, () => {});
    void loadGoogle();
    // Read again with the meetings: the banner goes as soon as the account is signed in again.
    const t = setInterval(() => (void loadMeetings(), void loadGoogle()), 5000);
    return () => clearInterval(t);
  }, []);

  // The open meeting is its row in the list, read again every five seconds.
  const selected = meetings.find((m) => m.id === selectedId) ?? null;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // A meeting older than the list opens on its own page, which says whether you are in it.
  const open = (id: string) => (meetings.some((m) => m.id === id) ? setSelectedId(id) : navigate(`/meetings/${id}`));

  return (
    <div>
      <PageHeader
        title="Meetings"
        subtitle={
          <>
            The <b>Pandas</b> bot joins Google Meet calls, takes notes from captions, chat and shared screens, answers questions asked to “Pandito”, and the Meeting Notes agent writes the minutes.
            {!enabled && <span className="mt-1 block text-warning">The meeting bot service is not configured (MEETING_BOT_URL and MEETING_BOT_SECRET).</span>}
          </>
        }
      />
      <div className="px-4 md:px-6">
        <Tabs
          label="Section"
          value={tab}
          onChange={setTab}
          tabs={[
            { key: "upcoming", label: "Upcoming" },
            { key: "history", label: "History", count: meetings.length },
            { key: "settings", label: "Settings" },
          ]}
        />
      </div>
      <div className="px-4 py-4 md:px-6">
        <GoogleSignInBanner status={google} onSignIn={() => setTab("settings")} />
        <ErrorText error={error} />
        {tab === "upcoming" && calendar && <Upcoming view={calendar} busy={busy} run={run} reload={loadCalendar} onOpen={open} />}
        {tab === "history" && <History meetings={meetings} onOpen={open} run={run} reload={loadMeetings} busy={busy} />}
        {tab === "settings" && calendar && <Settings view={calendar} busy={busy} onSave={(s) => run(async () => setCalendar(await put<CalendarView>("/api/meetings/calendar/settings", s)))} />}
      </div>
      <MeetingModal meeting={selected} onClose={() => setSelectedId(null)} onLeave={(id) => run(async () => { await post<MeetingDTO>(`/api/meetings/${id}/leave`); await loadMeetings(); })}
        onRetry={(id) => run(async () => { await post<MeetingDTO>(`/api/meetings/${id}/retry`); await loadMeetings(); })}
        busy={busy}
      />
    </div>
  );
}

/** Calendar descriptions are HTML: turn them into plain text, keeping link targets. Parsed inertly (no scripts run). */
function descriptionText(html: string): string {
  const doc = new DOMParser().parseFromString(html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h\d)>/gi, "\n"), "text/html");
  doc.querySelectorAll("a").forEach((a) => {
    const href = a.getAttribute("href");
    if (href && a.textContent?.trim() !== href) a.textContent = `${a.textContent} (${href})`;
  });
  return (doc.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
}

/** Plain text with clickable http(s) links. */
function Linkified({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s)]+)/g);
  return <>{parts.map((part, i) => (/^https?:\/\//.test(part) ? <TextLink key={i} href={part} className="break-all">{part}</TextLink> : <span key={i}>{part}</span>))}</>;
}

/** Auto-join on: everything not excluded. Off: only meetings an admin scheduled. */
export const willJoin = (e: MeetingEventDTO, autoJoin: boolean) => (autoJoin ? !e.excluded : e.excludedOverride === false);

const RESPONSE: Record<MeetingEventDTO["attendees"][number]["responseStatus"], { label: string; tone: "emerald" | "red" | "amber" | "zinc" }> = {
  accepted: { label: "accepted", tone: "emerald" }, declined: { label: "declined", tone: "red" }, tentative: { label: "maybe", tone: "amber" }, needsAction: { label: "no reply", tone: "zinc" },
};

function EventModal({ event, autoJoin, busy, onClose, onExclude, onJoin, onOpenMeeting }: {
  event: MeetingEventDTO | null; autoJoin: boolean; busy: boolean; onClose: () => void;
  onExclude: (e: MeetingEventDTO, excluded: boolean | null) => void; onJoin: (e: MeetingEventDTO) => void; onOpenMeeting: (id: string) => void;
}) {
  // A meeting Pandas has not joined yet: the rules on the left, the ways to send it on the right.
  const open = event && !event.meetingId ? event : null;
  const joinable = open !== null && open.platform !== "unknown";
  return (
    <Dialog
      open={Boolean(event)}
      onClose={onClose}
      size="lg"
      title={event?.title ?? "Meeting"}
      cancel={false}
      secondary={
        open &&
        (autoJoin ? (
          <>
            {open.excluded ? (
              <Button disabled={busy} onClick={() => onExclude(open, false)}>Include</Button>
            ) : (
              <Button disabled={busy} onClick={() => onExclude(open, true)}>Exclude</Button>
            )}
            {open.excludedOverride !== null && <Button variant="ghost" disabled={busy} onClick={() => onExclude(open, null)}>Use rules</Button>}
          </>
        ) : (
          joinable && willJoin(open, false) && <Button disabled={busy} onClick={() => onExclude(open, null)}>Unschedule</Button>
        ))
      }
      primary={
        joinable && (
          <>
            {!autoJoin && !willJoin(open, false) && <Button variant="primary" disabled={busy} onClick={() => onExclude(open, false)}>Schedule Pandas</Button>}
            <Button variant="primary" disabled={busy} onClick={() => onJoin(open)}>Send Pandas now</Button>
          </>
        )
      }
    >
      {event && (
        <div className="space-y-4 text-sm">
          <div className="space-y-1">
            <div>{when(event.startsAt)} – {time(event.endsAt)} · {duration(event.startsAt, event.endsAt)}</div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
              <TextLink href={event.meetingUrl}>Open {PLATFORM_NAME[event.platform]}</TextLink>
              {event.htmlLink && <TextLink href={event.htmlLink}>Open in Google Calendar</TextLink>}
              <span className="text-muted">organizer {event.organizerEmail ?? "unknown"}</span>
              {event.calendarCount > 1 && <span className="text-muted">in {event.calendarCount} watched calendars</span>}
            </div>
            <div className="text-xs">
              Pandas:{" "}
              {event.meetingId ? (
                <TextLink onClick={() => onOpenMeeting(event.meetingId!)}>{event.meetingStatus ?? "requested"}</TextLink>
              ) : event.joinError ? (
                <span className="text-danger">couldn't join: {event.joinError}</span>
              ) : event.platform === "unknown" ? (
                <span className="text-muted">no Google Meet, Zoom or Teams link on this event</span>
              ) : willJoin(event, autoJoin) ? (
                <span className="text-success">will join a minute before it starts{autoJoin ? "" : " (scheduled)"}</span>
              ) : autoJoin ? (
                <span className="text-muted">skipped{event.excludedOverride === true ? " (by admin)" : event.excludedByRule ? ` (${event.excludedByRule})` : ""}</span>
              ) : (
                <span className="text-muted">not scheduled (auto-join is off)</span>
              )}
            </div>
          </div>

          <List title={`Guests (${event.attendees.length})`} empty="No guest list.">
            {event.attendees.map((a) => (
              <ListRow
                key={a.email}
                title={
                  <>
                    <span className="min-w-0 truncate">{a.name || a.email}</span>
                    {a.organizer && <Badge tone="blue">organizer</Badge>}
                    {a.optional && <Badge>optional</Badge>}
                    <Badge tone={RESPONSE[a.responseStatus].tone}>{RESPONSE[a.responseStatus].label}</Badge>
                  </>
                }
                subtitle={a.name ? a.email : undefined}
              />
            ))}
          </List>

          {event.attachments.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-muted">Attachments</div>
              <ul className="space-y-1">
                {event.attachments.map((a) => (
                  <li key={a.fileUrl}>
                    <Icon name="paperclip" className="mr-1 inline size-3.5 text-faint" /><TextLink href={a.fileUrl} newTab>{a.title}</TextLink>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className="mb-1 text-xs font-medium text-muted">Description</div>
            {event.description.trim() ? (
              <div className="rounded-md bg-fill-subtle p-3 whitespace-pre-wrap"><Linkified text={descriptionText(event.description)} /></div>
            ) : (
              <p className="text-xs text-faint">No description.</p>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

function Upcoming({ view, busy, run, reload, onOpen }: { view: CalendarView; busy: boolean; run: (fn: () => Promise<unknown>) => Promise<void>; reload: () => Promise<void>; onOpen: (id: string) => void }) {
  const { settings, status, events } = view;
  // "Only internal meetings" auto-joins on its own; the external ones are skipped by rule.
  const autoJoin = settings.autoJoin || settings.internalOnly;
  const [openId, setOpenId] = useState<string | null>(null);
  const openEvent = events.find((e) => e.id === openId) ?? null;
  const setExcluded = (e: MeetingEventDTO, excluded: boolean | null) => run(async () => { await patch(`/api/meetings/events/${e.id}`, { excluded }); await reload(); });
  const joinNow = (e: MeetingEventDTO) => run(async () => { await post(`/api/meetings/events/${e.id}/join`); await reload(); });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={autoJoin ? "emerald" : "zinc"}>{settings.internalOnly ? "auto-join internal" : autoJoin ? "auto-join on" : "auto-join off"}</Badge>
        <span className="min-w-0 break-words text-muted">
          {settings.calendars.length ? `Watching ${settings.calendars.join(", ")}` : "No calendars watched"}
          {status.lastSyncAt && ` · synced ${timeAgo(status.lastSyncAt)}`}
        </span>
        <Button size="sm" className="ml-auto" disabled={busy || !settings.calendars.length} onClick={() => run(async () => { await post("/api/meetings/calendar/sync"); await reload(); })}>
          Sync now
        </Button>
      </div>
      {status.problem && <p className="rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">{status.problem}</p>}
      {status.lastSyncError && <p className="text-sm text-danger">Last sync failed: {status.lastSyncError}</p>}
      <Table
        rows={events}
        rowKey={(e) => e.id}
        onRowClick={(e) => setOpenId(e.id)}
        rowClassName={(e) => {
          const running = Boolean(e.meetingStatus && ACTIVE.includes(e.meetingStatus));
          const scheduled = !e.meetingId && !e.joinError && e.platform !== "unknown" && willJoin(e, autoJoin);
          return running || scheduled ? "bg-success/10" : e.excluded ? "text-faint" : "";
        }}
        empty="No upcoming meetings with a video link in the watched calendars."
        columns={[
          {
            header: "When",
            cell: (e) => {
              const index = events.indexOf(e);
              const newDay = index === 0 || new Date(events[index - 1]!.startsAt).toDateString() !== new Date(e.startsAt).toDateString();
              return (
                <>
                  {newDay && <div className="mb-0.5 max-w-52 text-xs font-semibold whitespace-normal text-muted">{dayLabel(e.startsAt)}</div>}
                  <div className="flex items-center gap-2">
                    {time(e.startsAt)} – {time(e.endsAt)}
                    <span className="rounded bg-fill px-1.5 py-0.5 text-tiny font-medium text-muted">{duration(e.startsAt, e.endsAt)}</span>
                  </div>
                </>
              );
            },
          },
          {
            header: "Meeting",
            primary: true,
            cell: (e) => (
              <>
                <div className="font-medium lg:truncate">{e.title}</div>
                <div className="mt-0.5 truncate text-xs text-muted">
                  {PLATFORM_NAME[e.platform]} · {e.attendeeCount} guests · {e.calendarCount > 1 ? `in ${e.calendarCount} calendars` : e.calendarEmail}
                </div>
              </>
            ),
          },
          { header: "Organizer", className: "text-xs text-muted", cell: (e) => <div className="max-w-48 truncate">{e.organizerEmail ?? "—"}</div> },
          {
            header: "Pandas",
            className: "text-xs",
            cell: (e) => {
              const running = Boolean(e.meetingStatus && ACTIVE.includes(e.meetingStatus));
              return e.meetingId ? (
                <TextLink className={running ? "font-medium text-success" : ""} onClick={(ev) => { ev.stopPropagation(); onOpen(e.meetingId!); }}>
                  {running && "● "}{(e.meetingStatus ?? "requested").replace("_", " ")}
                </TextLink>
              ) : e.joinError ? (
                <span className="text-danger" title={e.joinError}>couldn't join</span>
              ) : e.platform === "unknown" ? (
                <span className="text-faint">no meeting link</span>
              ) : willJoin(e, autoJoin) ? (
                <span className="text-success">will join{!autoJoin ? " (scheduled)" : e.excludedOverride === false && e.excludedByRule ? " (included by admin)" : ""}</span>
              ) : autoJoin ? (
                <span>skipped{e.excludedOverride === true ? " (by admin)" : e.excludedByRule ? ` (${e.excludedByRule})` : ""}</span>
              ) : (
                <span className="text-muted">not scheduled</span>
              );
            },
          },
          {
            header: "",
            align: "right",
            className: "w-48",
            cell: (e) =>
              !e.meetingId && (
                <div className="-mr-2 flex flex-wrap justify-end" onClick={(ev) => ev.stopPropagation()}>
                  {!autoJoin ? (
                    e.platform !== "unknown" &&
                    (willJoin(e, false) ? (
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => setExcluded(e, null)}>Unschedule</Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="font-semibold" disabled={busy} onClick={() => setExcluded(e, false)}>Schedule</Button>
                    ))
                  ) : (
                    <>
                      {e.excluded ? (
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => setExcluded(e, false)}>Include</Button>
                      ) : (
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => setExcluded(e, true)}>Exclude</Button>
                      )}
                      {e.excludedOverride !== null && (
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => setExcluded(e, null)}>Use rules</Button>
                      )}
                    </>
                  )}
                  {e.platform !== "unknown" && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => joinNow(e)}>Join now</Button>
                  )}
                </div>
              ),
          },
        ]}
      />
      <EventModal
        event={openEvent}
        autoJoin={autoJoin}
        busy={busy}
        onClose={() => setOpenId(null)}
        onExclude={setExcluded}
        onJoin={joinNow}
        onOpenMeeting={(id) => { setOpenId(null); onOpen(id); }}
      />
    </div>
  );
}

function History({ meetings, onOpen, run, reload, busy }: { meetings: MeetingDTO[]; onOpen: (id: string) => void; run: (fn: () => Promise<unknown>) => Promise<void>; reload: () => Promise<void>; busy: boolean }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  return (
    <div className="space-y-4">
      <form
        className="flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => { await post("/api/meetings", { url, title }); setUrl(""); setTitle(""); await reload(); });
        }}
      >
        <input className={inputCls} placeholder="https://meet.google.com/abc-defg-hij" value={url} onChange={(e) => setUrl(e.target.value)} />
        <input className={`${inputCls} sm:max-w-xs`} placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Button variant="primary" disabled={busy || !url.trim()} className="whitespace-nowrap">Send Pandas</Button>
      </form>
      <Table
        rows={meetings}
        rowKey={(m) => m.id}
        onRowClick={(m) => onOpen(m.id)}
        empty="No meetings yet."
        columns={[
          {
            header: "Meeting",
            primary: true,
            cell: (m) => m.private ? (
              <>
                <div className="flex min-w-0 items-center gap-2 text-muted">
                  <Icon name="key" className="size-3.5 shrink-0" />
                  <span className="truncate">A meeting you're not in</span>
                </div>
                <div className="mt-0.5 truncate text-xs text-faint">{PLATFORM_NAME[m.platform]} · {members(m.memberCount ?? 0)}</div>
              </>
            ) : (
              <>
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate font-medium">{m.title || "(untitled)"}</span>
                  {/* Shared outside the platform: the meeting has a public page. */}
                  {m.publishedAt && (
                    <Badge tone="emerald">
                      <span className="inline-flex items-center gap-1">
                        <Icon name="link" className="size-3" /> live page
                      </span>
                    </Badge>
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs text-muted">{m.url}</div>
              </>
            ),
          },
          { header: "Status", className: "text-xs text-muted", cell: (m) => <span><StatusBadge status={STATUS_TONE[m.status]} /> {statusWords(m)}</span> },
          {
            // Why the bot left and how many it saw (AIT-364): not the meeting's content, so on every row.
            header: "Ended",
            className: "text-xs text-muted",
            cell: (m) => (m.endReason ? [END_WORDS[m.endReason], peopleWords(m)].filter(Boolean).join(" · ") : "—"),
          },
          { header: "For", className: "text-muted", cell: (m) => (m.private ? "—" : m.requestedByName) },
          {
            header: "Captured",
            className: "text-xs text-muted",
            cell: (m) => (m.private ? "—" : `${m.transcriptCount} lines · ${m.frames.length} screens${m.audio.chunks > 0 ? ` · audio ${m.audio.done}/${m.audio.chunks}` : ""}`),
          },
          { header: "When", className: "text-xs text-muted", cell: (m) => timeAgo(m.createdAt) },
        ]}
      />
    </div>
  );
}

function Settings({ view, busy, onSave }: { view: CalendarView; busy: boolean; onSave: (s: MeetingCalendarSettings) => void }) {
  const [autoJoin, setAutoJoin] = useState(view.settings.autoJoin);
  const [calendars, setCalendars] = useState(view.settings.calendars.join("\n"));
  const [keywords, setKeywords] = useState(view.settings.excludeKeywords.join("\n"));
  const [organizers, setOrganizers] = useState(view.settings.excludeOrganizers.join("\n"));
  const [internalOnly, setInternalOnly] = useState(view.settings.internalOnly);
  const [internalDomains, setInternalDomains] = useState(view.settings.internalDomains.join(", "));
  const [fallback, setFallback] = useState(view.settings.fallbackUserEmail ?? "");
  const [sendMinutes, setSendMinutes] = useState(view.settings.sendMinutes);
  const [captionLanguage, setCaptionLanguage] = useState(view.settings.captionLanguage);
  const [transcription, setTranscription] = useState(view.settings.transcription);
  const [botName, setBotName] = useState(view.settings.botName);
  const lines = (s: string) => s.split("\n").map((l) => l.trim()).filter(Boolean);
  return (
    <div className="max-w-2xl space-y-4">
      <GoogleAccountPanel />
      <p className="text-sm text-muted">
        Pandas reads upcoming events from these calendars with the Google Workspace connector{view.status.connector ? ` (${view.status.connector})` : ""}. The simplest setup:
        watch the Pandas account's calendar, and invite Pandas to meetings it should attend.
      </p>
      <Switch label="Join upcoming meetings automatically" checked={autoJoin} onChange={setAutoJoin} />
      <Switch
        label="Join internal meetings automatically"
        hint="Only meetings where the organizer and every guest are on these domains; the rest are skipped. You can still include one by hand."
        checked={internalOnly}
        onChange={setInternalOnly}
      />
      {internalOnly && (
        <Field label="Internal email domains" hint="Comma-separated, e.g. pandas.io">
          <input className={inputCls} value={internalDomains} onChange={(e) => setInternalDomains(e.target.value)} placeholder="pandas.io" />
        </Field>
      )}
      <Switch
        label="Send minutes after meetings"
        hint="Pandito tells each Pandas person in a meeting, by Slack DM or in their chat with it, where the minutes are and what their own action items are. When off, nothing is sent; minutes are still written and kept on each meeting's page."
        checked={sendMinutes}
        onChange={setSendMinutes}
      />
      <Field label="Meeting language (captions)" hint="Pandas sets this language for Meet's live captions. Captions follow one spoken language at a time.">
        <select className={inputCls} value={captionLanguage} onChange={(e) => setCaptionLanguage(e.target.value)}>
          {["English", "Greek", "French", "German", "Spanish", "Italian", "Portuguese", "Dutch", "Arabic", "Turkish"].map((l) => <option key={l}>{l}</option>)}
        </select>
      </Field>
      <Field
        label="Transcription"
        hint="Recording the audio handles meetings that mix languages (Greek, English, French…). Captions are still read for live questions and to name speakers, and cover any audio that can't be transcribed."
      >
        <select className={inputCls} value={transcription} onChange={(e) => setTranscription(e.target.value as MeetingCalendarSettings["transcription"])}>
          <option value="captions">Meet captions only (one language)</option>
          <option value="gemini">Record audio · Gemini 3.5 Transcribe (Google Gemini API call)</option>
          <option value="openai">Record audio · OpenAI gpt-4o-transcribe-diarize (OpenAI API call)</option>
          <option value="whisper">Record audio · self-hosted Whisper (WHISPER_URL)</option>
        </select>
      </Field>
      <Field label="Bot name" hint="What everyone sees in the participant list. Zoom and Teams ask a guest for a name before letting it in.">
        <input className={inputCls} value={botName} onChange={(e) => setBotName(e.target.value)} placeholder="Pandas" />
      </Field>
      <Field label="Calendars to watch" hint='One Workspace email per line. "*" watches everyone with a Pandas account.'>
        <textarea className={`${inputCls} font-mono text-xs`} rows={3} value={calendars} onChange={(e) => setCalendars(e.target.value)} placeholder="pandas@pandas.io" />
      </Field>
      <Field label="Skip meetings whose title contains" hint="One per line, e.g. 1:1, interview, HR, confidential">
        <textarea className={`${inputCls} font-mono text-xs`} rows={3} value={keywords} onChange={(e) => setKeywords(e.target.value)} />
      </Field>
      <Field label="Skip meetings organized by" hint="Emails or domains, one per line, e.g. ceo@pandas.io or @partner.com">
        <textarea className={`${inputCls} font-mono text-xs`} rows={3} value={organizers} onChange={(e) => setOrganizers(e.target.value)} />
      </Field>
      <Field label="Send minutes to (when the organizer has no Pandas account)">
        <input className={inputCls} value={fallback} onChange={(e) => setFallback(e.target.value)} placeholder="ops@pandas.io" />
      </Field>
      <Button
        variant="primary"
        disabled={busy}
        onClick={() =>
          onSave({
            autoJoin, calendars: lines(calendars), excludeKeywords: lines(keywords), excludeOrganizers: lines(organizers),
            internalOnly, internalDomains: internalDomains.split(/[\s,]+/).map((d) => d.trim()).filter(Boolean),
            fallbackUserEmail: fallback.trim() || null, sendMinutes, captionLanguage, transcription, botName: botName.trim() || "Pandas",
          })
        }
      >
        Save settings
      </Button>
    </div>
  );
}

/** "1 person", "4 people": how many are in a meeting, its guests never counted. */
const members = (n: number) => `${n} ${n === 1 ? "person" : "people"} in it`;

/**
 * The bot's controls for one meeting (AIT-335): its status and error, and Retry or Leave. What it holds is on its own
 * page, for the people in it; to anyone else this says only that it happened.
 */
function MeetingModal({ meeting, onClose, onLeave, onRetry, busy }: { meeting: MeetingDTO | null; onClose: () => void; onLeave: (id: string) => void; onRetry: (id: string) => void; busy: boolean }) {
  const active = meeting && ACTIVE.includes(meeting.status);
  return (
    <Dialog
      open={Boolean(meeting)}
      onClose={onClose}
      size="md"
      title={meeting ? (meeting.private ? "A meeting you're not in" : meeting.title || "Meeting") : "Meeting"}
      description={meeting?.private ? "Only the people in it open what it holds. You still run the bot for it." : undefined}
      cancel={false}
      secondary={meeting && !meeting.private && <ButtonLink to={`/meetings/${meeting.id}`}>Open the meeting</ButtonLink>}
      primary={
        active ? (
          <Button variant="danger" disabled={busy} onClick={() => onLeave(meeting!.id)}>Ask Pandas to leave</Button>
        ) : meeting?.status === "failed" ? (
          <Button disabled={busy} onClick={() => onRetry(meeting.id)}>Retry joining</Button>
        ) : undefined
      }
    >
      {meeting && (
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
            <span><StatusBadge status={STATUS_TONE[meeting.status]} /> {statusWords(meeting)}</span>
            <span>{PLATFORM_NAME[meeting.platform]}</span>
            <span>{members(meeting.memberCount ?? 0)}</span>
            {!meeting.private && <span>for {meeting.requestedByName}</span>}
            <span>sent {when(meeting.createdAt)}</span>
            {meeting.joinedAt && <span>joined {when(meeting.joinedAt)}</span>}
            {meeting.endedAt && <span>ended {when(meeting.endedAt)}</span>}
            {!meeting.private && meeting.notesConversationId && <TextLink to={`/admin/conversations/${meeting.notesConversationId}`}>notes run</TextLink>}
          </div>
          {meeting.error && <p className="text-danger">{meeting.error}</p>}
          <MeetingEnd meeting={meeting} />
          {active && meeting.captionsStoppedAt && (
            <p className="rounded-md bg-warning/10 px-3 py-2 text-warning">
              Live captions stopped at {new Date(meeting.captionsStoppedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })} — voice questions are picked up from the audio
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
